import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, Observable } from 'rxjs';
import { DataTableComponent, TableColumn } from
  '@shared/components/data-table/data-table.component';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { MessageService } from 'primeng/api';
import { UserBusinessUnitsService } from
  '@app/api/user-service/api/userBusinessUnits.service';
import { UserBusinessUnitAssignRequest } from
  '@app/api/user-service/model/userBusinessUnitAssignRequest';
import { PermissionService } from '@app/core/services/permission.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-business-unit-management',
  standalone: true,
  imports: [
    CommonModule,
    TagModule,
    TooltipModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    AppButtonComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './business-unit-management.component.html',
  styleUrl: './business-unit-management.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessUnitManagementComponent implements OnChanges {
  @Input() users: Array<Record<string, unknown>> = [];
  @Input() selectedUnitId: string | null = null;
  @Input() selectedUnitName: string | null = null;
  @Input() usersLoading = false;
  @Input() canAssignUsers = true;

  @Output() userUnassigned = new EventEmitter<void>();
  @Output() assignUser = new EventEmitter<void>();
  @Output() userSearchChange = new EventEmitter<string>();

  readonly usersSignal = signal<Array<Record<string, unknown>>>([]);
  readonly selectedUnitNameSignal = signal<string | null>(null);
  readonly usersLoadingSignal = signal(false);
  readonly canAssignUsersSignal = signal(true);
  readonly unassigningUserId = signal<string | null>(null);
  readonly selectedUsers = signal<Array<Record<string, unknown>>>([]);
  readonly isUnassigningMultiple = signal(false);

  readonly unassignConfirmVisible = signal(false);
  readonly unassignConfirmTitle = signal('');
  readonly unassignConfirmMessage = signal('');
  readonly unassignConfirmButtons = signal<ConfirmationButton[]>([]);
  private pendingUnassignSingle: {
    user: Record<string, unknown>;
    businessUnitId: number;
  } | null = null;
  private pendingUnassignMultiple: {
    selected: Array<Record<string, unknown>>;
    businessUnitId: number;
  } | null = null;

  readonly userSearchConfig: FormFieldConfig[] = [
    {
      key: 'keyword',
      label: 'configuration.business_unit.search.search_user',
      type: 'text',
      placeholder: 'configuration.business_unit.search.search_user_placeholder',
      width: '280px',
    },
  ];

  private readonly destroyRef = inject(DestroyRef);
  private readonly userBusinessUnitsService = inject(UserBusinessUnitsService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);

  // Permission check - method instead of computed since canDoAction is not signal-based
  canUpdate(): boolean {
    return this.permissionService.canDoAction('settings.user.business_unit.edit');
  }

  readonly userColumns = computed((): TableColumn[] => [
    {
      label: 'configuration.business_unit.table.name',
      prop: 'name',
      type: 'text',
      width: 200,
    },
    {
      label: 'configuration.business_unit.table.username',
      prop: 'username',
      type: 'text',
      width: 150,
    },
    {
      label: 'configuration.business_unit.table.email',
      prop: 'email',
      type: 'text',
      width: 250,
    },
    {
      label: 'configuration.business_unit.table.role',
      prop: 'role',
      type: 'text',
      width: 150,
    },
    {
      label: 'configuration.business_unit.table.status',
      prop: 'status',
      type: 'status',
      width: 120,
      align: 'center',
    },
    {
      label: 'configuration.business_unit.table.actions',
      prop: 'actions',
      type: 'actions',
      width: 150,
      align: 'center',
    },
  ]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users']) {
      this.usersSignal.set(this.users);
      this.selectedUsers.set([]);
    }
    if (changes['selectedUnitName']) {
      this.selectedUnitNameSignal.set(this.selectedUnitName);
    }
    if (changes['usersLoading']) {
      this.usersLoadingSignal.set(this.usersLoading);
    }
    if (changes['canAssignUsers']) {
      this.canAssignUsersSignal.set(this.canAssignUsers);
    }
  }

  onUserSearch(formData: SearchFormData): void {
    const value = (formData['keyword'] ?? '').toString().trim();
    this.userSearchChange.emit(value);
  }

  onUserSearchReset(): void {
    this.userSearchChange.emit('');
  }

  onSelectionChange(selection: Array<Record<string, unknown>>): void {
    this.selectedUsers.set(selection);
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'warn' | 'info' | 'contrast' | 'danger' {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === 'active') {
      return 'success';
    }

    if (
      normalizedStatus === 'inactive' ||
      normalizedStatus === 'không hoạt động'
    ) {
      return 'danger';
    }

    if (normalizedStatus === 'chưa kết nối') {
      return 'warn';
    }

    return 'info';
  }

  onUnassignUser(user: Record<string, unknown>): void {
    if (!this.selectedUnitId) {
      return;
    }

    const businessUnitId = this.parseUnitId(this.selectedUnitId);
    if (Number.isNaN(businessUnitId)) {
      console.error(
        '[BusinessUnitManagementComponent] Invalid business unit ID:',
        this.selectedUnitId,
      );
      return;
    }

    const originalUser = user['_original'] as
      | { id?: string; email?: string; name?: string; username?: string }
      | undefined;
    if (!originalUser) {
      console.error(
        '[BusinessUnitManagementComponent] User data missing _original field',
      );
      return;
    }

    const userId = originalUser.id;
    const email = originalUser.email;
    if (!userId && !email) {
      console.error(
        '[BusinessUnitManagementComponent] User ID and email are both missing',
      );
      return;
    }

    const displayName =
      originalUser.name ||
      originalUser.username ||
      email ||
      userId ||
      '';

    const message = this.translateService.instant(
      'configuration.business_unit.dialog.unassign_user_confirm',
      { name: displayName },
    );

    this.pendingUnassignSingle = { user, businessUnitId };
    this.pendingUnassignMultiple = null;
    this.unassignConfirmTitle.set(
      this.translateService.instant(
        'configuration.business_unit.dialog.unassign_user_confirm_title',
      ),
    );
    this.unassignConfirmMessage.set(message);
    this.unassignConfirmButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.confirm'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.unassignConfirmVisible.set(true);
  }

  private executeUnassignUser(
    user: Record<string, unknown>,
    businessUnitId: number,
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ): void {
    const originalUser = user['_original'] as
      | { id?: string; email?: string }
      | undefined;
    if (!originalUser) return;

    const userId = originalUser.id;
    const email = originalUser.email;
    if (!userId && !email) return;

    const request: UserBusinessUnitAssignRequest = {
      businessUnitId,
      userId: userId || undefined,
      email: email || undefined,
    };

    if (userId) {
      this.unassigningUserId.set(userId);
    } else if (email) {
      this.unassigningUserId.set(email);
    }

    this.userBusinessUnitsService
      .unassignUserFromBusinessUnit(request, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        finalize(() => this.unassigningUserId.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.userUnassigned.emit();
          callbacks?.onSuccess?.();
        },
        error: (error: unknown) => {
          console.error(
            '[BusinessUnitManagementComponent] Error unassigning user:',
            error,
          );
          callbacks?.onError?.();
        },
      });
  }

  isUnassigning(user: Record<string, unknown>): boolean {
    const originalUser = user['_original'] as
      | { id?: string; email?: string }
      | undefined;
    if (!originalUser) {
      return false;
    }

    const userId = originalUser.id;
    const email = originalUser.email;
    const unassigningId = this.unassigningUserId();

    return (
      unassigningId !== null &&
      (unassigningId === userId || unassigningId === email)
    );
  }

  onUnassignUsers(): void {
    const selected = this.selectedUsers();
    if (selected.length === 0 || !this.selectedUnitId) {
      return;
    }

    const businessUnitId = this.parseUnitId(this.selectedUnitId);
    if (Number.isNaN(businessUnitId)) {
      console.error(
        '[BusinessUnitManagementComponent] Invalid business unit ID:',
        this.selectedUnitId,
      );
      return;
    }

    const count = selected.length;
    const message = this.translateService.instant(
      'configuration.business_unit.dialog.unassign_users_confirm',
      { count },
    );

    this.pendingUnassignSingle = null;
    this.pendingUnassignMultiple = { selected, businessUnitId };
    this.unassignConfirmTitle.set(
      this.translateService.instant(
        'configuration.business_unit.dialog.unassign_users_confirm_title',
      ),
    );
    this.unassignConfirmMessage.set(message);
    this.unassignConfirmButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.confirm'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.unassignConfirmVisible.set(true);
  }

  onUnassignConfirmAction(action: string): void {
    if (action === 'cancel') {
      this.closeUnassignConfirm();
      return;
    }

    if (action === 'confirm') {
      this.setUnassignConfirmButtonsLoading(true);

      if (this.pendingUnassignSingle) {
        const { user, businessUnitId } = this.pendingUnassignSingle;
        this.executeUnassignUser(user, businessUnitId, {
          onSuccess: () => {
            this.closeUnassignConfirm();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'configuration.business_unit.dialog.unassign_user_success',
              ),
            });
          },
          onError: () => {
            this.closeUnassignConfirm();
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'configuration.business_unit.dialog.unassign_user_failed',
              ),
            });
          },
        });
      } else if (this.pendingUnassignMultiple) {
        const { selected, businessUnitId } = this.pendingUnassignMultiple;
        this.executeUnassignUsers(selected, businessUnitId, {
          onSuccess: () => {
            this.closeUnassignConfirm();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'configuration.business_unit.dialog.unassign_user_success',
              ),
            });
          },
          onError: () => {
            this.closeUnassignConfirm();
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'configuration.business_unit.dialog.unassign_users_failed',
              ),
            });
          },
        });
      }
    }
  }

  onUnassignConfirmVisibleChange(visible: boolean): void {
    this.unassignConfirmVisible.set(visible);
    if (!visible) {
      this.pendingUnassignSingle = null;
      this.pendingUnassignMultiple = null;
      this.setUnassignConfirmButtonsLoading(false);
    }
  }

  private setUnassignConfirmButtonsLoading(loading: boolean): void {
    this.unassignConfirmButtons.update((buttons) =>
      buttons.map((btn) =>
        btn.action === 'confirm'
          ? { ...btn, loading, disabled: loading }
          : { ...btn, disabled: loading },
      ),
    );
  }

  private closeUnassignConfirm(): void {
    this.setUnassignConfirmButtonsLoading(false);
    this.unassignConfirmVisible.set(false);
    this.pendingUnassignSingle = null;
    this.pendingUnassignMultiple = null;
  }

  private executeUnassignUsers(
    selected: Array<Record<string, unknown>>,
    businessUnitId: number,
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ): void {
    this.isUnassigningMultiple.set(true);

    const unassignRequests: Array<Observable<unknown>> = [];

    selected.forEach((user) => {
      const originalUser = user['_original'] as
        | { id?: string; email?: string }
        | undefined;
      if (!originalUser) return;

      const userId = originalUser.id;
      const email = originalUser.email;
      if (!userId && !email) return;

      const request: UserBusinessUnitAssignRequest = {
        businessUnitId,
        userId: userId || undefined,
        email: email || undefined,
      };

      unassignRequests.push(
        this.userBusinessUnitsService.unassignUserFromBusinessUnit(
          request,
          'body',
          false,
          { httpHeaderAccept: 'application/json' as any },
        ),
      );
    });

    if (unassignRequests.length === 0) {
      this.isUnassigningMultiple.set(false);
      return;
    }

    forkJoin(unassignRequests)
      .pipe(
        finalize(() => this.isUnassigningMultiple.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.selectedUsers.set([]);
          this.userUnassigned.emit();
          callbacks?.onSuccess?.();
        },
        error: (error: unknown) => {
          console.error(
            '[BusinessUnitManagementComponent] Error unassigning users:',
            error,
          );
          callbacks?.onError?.();
        },
      });
  }

  onAssignUser(): void {
    this.assignUser.emit();
  }

  private parseUnitId(unitId: string): number {
    const parsed = Number.parseInt(unitId, 10);
    return Number.isNaN(parsed) ? Number.NaN : parsed;
  }
}
